import { Button, Descriptions, Modal, Space } from 'antd';
import { Camera } from '@phosphor-icons/react';
import { StatusTag } from '../../components/StatusTag';
import type { BillScanListItem } from './api';
import { billStatusMeta, flagMeta } from './flagLabels';
import { VND } from '../../format';
import { tokens } from '../../theme';


function formatVnDateTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`;
}

interface Props {
  scan: BillScanListItem | null;
  onClose: () => void;
  onViewImage: (billScanId: string) => void;
}

/**
 * Chi tiết một hóa đơn quét — dựng hoàn toàn từ dữ liệu dòng đã có trong danh sách
 * (không gọi endpoint mới). Hiện MỌI cờ, kể cả cờ kỹ thuật bị ẩn ở cột danh sách.
 */
export function BillScanDetailModal({ scan, onClose, onViewImage }: Props) {
  const status = scan ? billStatusMeta(scan.status) : null;

  return (
    <Modal
      open={scan !== null}
      title={scan ? `Hóa đơn ${scan.billRef}` : ''}
      footer={
        scan?.hasImage ? (
          <Space>
            <Button
              icon={<Camera size={15} />}
              onClick={() => {
                onViewImage(scan.id);
              }}
            >
              Xem ảnh hóa đơn
            </Button>
            <Button type="primary" onClick={onClose}>
              Đóng
            </Button>
          </Space>
        ) : (
          <Button type="primary" onClick={onClose}>
            Đóng
          </Button>
        )
      }
      onCancel={onClose}
      destroyOnHidden
      width={640}
    >
      {scan && status && (
        <Descriptions column={2} size="small" bordered styles={{ label: { width: 150 } }}>
          <Descriptions.Item label="Trạng thái">
            <StatusTag color={status.color}>{status.label}</StatusTag>
          </Descriptions.Item>
          <Descriptions.Item label="Nguồn quét">
            {scan.source === 'Member' ? 'Khách tự quét' : `Nhân viên: ${scan.staffName ?? '—'}`}
          </Descriptions.Item>
          <Descriptions.Item label="Chi nhánh">{scan.branchName}</Descriptions.Item>
          <Descriptions.Item label="Khách">
            {scan.memberName || '—'} · {scan.memberCode}
          </Descriptions.Item>
          <Descriptions.Item label="Số tiền">
            <b style={{ fontVariantNumeric: 'tabular-nums' }}>{VND.format(scan.amount)}đ</b>
          </Descriptions.Item>
          <Descriptions.Item label="Điểm đã cộng">
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{VND.format(scan.pointsAwarded)}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Thời điểm quét">{formatVnDateTime(scan.scannedAt)}</Descriptions.Item>
          <Descriptions.Item label="Giờ in hóa đơn">
            {scan.billIssuedAt ? formatVnDateTime(scan.billIssuedAt) : 'Không có (QR không mang giờ)'}
          </Descriptions.Item>
          <Descriptions.Item label="Thời điểm xác nhận">
            {scan.confirmedAt ? formatVnDateTime(scan.confirmedAt) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Lý do từ chối">{scan.rejectReason ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Cờ cảnh báo" span={2}>
            {scan.flags.length === 0 ? (
              <span style={{ color: tokens.color.ink3 }}>Không có cờ</span>
            ) : (
              <Space size={[4, 4]} wrap>
                {scan.flags.map((code) => {
                  const meta = flagMeta(code);
                  return (
                    <StatusTag
                      key={code}
                      color={meta.tone === 'danger' ? 'error' : meta.tone === 'warn' ? 'warning' : 'blue'}
                    >
                      {meta.label}
                    </StatusTag>
                  );
                })}
              </Space>
            )}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Modal>
  );
}
